/**
 * The answer surface for a turn where the agent asked the user to decide:
 * the question line, one tappable chip per option, and a hint pointing at the
 * compose box for a free-text answer. Replaces the passive "You could ask:"
 * suggestions row for that turn. Single-select chips send on tap; when the
 * question is `multi`, taps toggle a selection and an "Add n ✓" confirm chip
 * sends the picked array instead. An option carrying an `action` (import-flow
 * entry points) never sends or toggles: its tap routes to `onOptionAction`. An
 * option carrying `accept` (target-price wizard stage keys) never sends either:
 * its tap routes to `onAccept`, which posts the confirmation for those keys.
 */
import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import type { ChatQuestion, ImportOptionAction } from '../../services/chatStream';

// The strategy question's enum options arrive as English enum values
// (Core/Growth/Speculative) even in zh sessions — the card's `duration` field
// must keep the English enum, so they cannot be localized at the source.
// This map is DISPLAY-ONLY: chips render the localized `portfolio.duration*`
// term, while every pick/toggle/confirm keeps the ORIGINAL string so the enum
// round-trips to the backend untouched (2026-10-04 user report). Free-form
// options (anything not exactly matching a key) pass through untouched.
const ENUM_OPTION_LABEL_KEYS: Record<string, string> = {
  Core: 'portfolio.durationCore',
  Growth: 'portfolio.durationGrowth',
  Speculative: 'portfolio.durationSpeculative',
};

export function optionDisplayLabel(
  option: string,
  t: (key: string, opts?: { defaultValue?: string }) => string
): string {
  // Object.hasOwn guard: a plain-object lookup inherits Object.prototype
  // members, so an option exactly named "constructor"/"toString" would
  // otherwise pick up a garbage "key" and render it.
  const key = Object.hasOwn(ENUM_OPTION_LABEL_KEYS, option)
    ? ENUM_OPTION_LABEL_KEYS[option]
    : undefined;
  return key ? t(key, { defaultValue: option }) : option;
}

export function QuestionChips({
  question,
  onPick,
  onConfirmMulti,
  onOptionAction,
  onAccept,
}: {
  question: ChatQuestion;
  onPick: (answer: string) => void;
  onConfirmMulti?: (picked: string[]) => void;
  /** Routes an action-option tap. *symbol* is the action's target instrument
   * (open_thesis_builder) — undefined for the import trio, which carry none. */
  onOptionAction?: (action: ImportOptionAction, symbol?: string | null) => void;
  /** Routes an accept-option tap (M5): the target-price wizard stage keys the
   * chip confirms. The handler posts an accept-only edit and sends NO chat
   * turn — the server's transition gate fires the advance turn, which IS the
   * reply. An absent handler keeps the tap inert, exactly like an action chip:
   * it must never degrade into sending the label as prose, because a prose
   * answer is not a stamp and the wizard would re-ask the same stage. */
  onAccept?: (keys: string[]) => void;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  // Multi-select state. A Set keeps re-taps a toggle, and spreading it for
  // the confirm callback preserves tap order in the payload.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const isMulti = question.multi === true;

  const toggle = (label: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  return (
    <View
      style={[
        styles.row,
        { borderColor: theme.colors.primary, backgroundColor: theme.colors.surface },
      ]}
    >
      <Text style={[styles.prompt, { color: theme.colors.text }]}>
        {question.prompt ?? t('chat.questionPrompt', { defaultValue: 'Your analyst asks:' })}
      </Text>
      <View style={styles.optionsRow}>
        {question.options.map((option) => {
          // Plain strings render the localized term — DISPLAY ONLY: the
          // payload (onPick/toggle/confirm) always carries the ORIGINAL
          // option string. Structured options render their own label, and
          // only the tap behavior differs when an action or accept is attached.
          const isString = typeof option === 'string';
          const raw = isString ? option : option.label;
          const label = isString ? optionDisplayLabel(option, t) : option.label;
          const action = isString ? undefined : option.action;
          const accept = isString ? undefined : option.accept;
          const picked = isMulti && selected.has(raw);
          const handlePress = () => {
            // Action options route to a UI flow INSTEAD of sending: no
            // onPick, no toggle. An absent handler keeps the tap inert —
            // it must never degrade into sending the label as a turn.
            if (action) {
              onOptionAction?.(action, isString ? undefined : option.symbol);
              return;
            }
            // Accept options route to the confirmation POST instead of
            // sending, for the same reason (M5). Action wins if a malformed
            // option somehow carried both — the server treats them as
            // mutually exclusive, so this is only belt-and-braces.
            if (accept?.length) {
              onAccept?.(accept);
              return;
            }
            if (isMulti) toggle(raw);
            else onPick(raw);
          };
          return (
            <Pressable
              key={label}
              onPress={handlePress}
              accessibilityRole="button"
              accessibilityLabel={label}
              accessibilityState={isMulti ? { selected: picked } : undefined}
              style={[
                styles.chip,
                { borderColor: theme.colors.primary },
                picked && { backgroundColor: theme.colors.selectedBackground },
              ]}
            >
              <Text style={[styles.chipText, { color: theme.colors.primary }]}>{label}</Text>
            </Pressable>
          );
        })}
        {isMulti ? (
          <Pressable
            onPress={() => onConfirmMulti?.([...selected])}
            disabled={selected.size === 0}
            accessibilityRole="button"
            accessibilityLabel={t('chat.multiConfirm', {
              defaultValue: `Add ${selected.size} ✓`,
              n: selected.size,
            })}
            style={[
              styles.chip,
              styles.confirmChip,
              {
                borderColor: theme.colors.primaryDark,
                backgroundColor: theme.colors.primary,
              },
              selected.size === 0 && styles.confirmDisabled,
            ]}
          >
            <Text style={[styles.chipText, { color: theme.colors.textInverse }]}>
              {/* The count is baked into the defaultValue so the label is
                  correct even if the locale key is missing; the n var drives
                  the {{n}} interpolation in the shipped locale strings. */}
              {t('chat.multiConfirm', {
                defaultValue: `Add ${selected.size} ✓`,
                n: selected.size,
              })}
            </Text>
          </Pressable>
        ) : null}
      </View>
      <Text style={[styles.hint, { color: theme.colors.textTertiary }]}>
        {t('chat.questionManualHint', { defaultValue: 'or type your own answer' })}
      </Text>
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    row: {
      borderRadius: theme.radii.md,
      borderWidth: 1,
      marginHorizontal: theme.spacing.md,
      marginBottom: theme.spacing.xs,
      padding: theme.spacing.sm,
    },
    prompt: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: theme.spacing.xs,
    },
    // 6 is off-scale on purpose: it is the exact gap of the suggestions row
    // this block replaces (ChatConversation), and the nearest tokens (xs=4,
    // sm=8) would visibly diverge from it.
    optionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    chip: {
      borderWidth: 1,
      borderRadius: theme.radii.xl,
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xxs,
    },
    chipText: { fontSize: theme.typography.fontSizeSm },
    // The multi-select confirm chip: same shape as an option chip, but filled
    // with the primary and a heavier border so it reads as the commit action.
    confirmChip: { borderWidth: 2 },
    confirmDisabled: { opacity: 0.5 },
    hint: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: theme.spacing.xs,
    },
  });
}
